import { SyncIcon } from "@hopper-ui/icons";
import {
    type ModuleRegisterFunction,
    NestedErrorBoundary,
    ProtectSidebarItemPriority,
    PublicLayout,
    registerProtectSidebarItem
} from "@sg-protect/shell";
import { Outlet } from "react-router";
import { ModuleAuthenticatedProviders } from "#components/module-providers/ModuleAuthenticatedProviders.tsx";
import { ModuleGlobalProviders } from "#components/module-providers/ModuleGlobalProviders.tsx";
import { getPaths } from "#paths.ts";
import { registerPrivateRoutes, registerPublicRoutes } from "./routes.tsx";

const registerRoutes: ModuleRegisterFunction = runtime => {
    runtime.registerRoute({
        element: (
            <ModuleGlobalProviders>
                <Outlet />
            </ModuleGlobalProviders>
        ),
        errorElement: <NestedErrorBoundary />,
        children: [
            {
                element: (
                    <PublicLayout>
                        <Outlet />
                    </PublicLayout>
                ),
                children: registerPublicRoutes()
            },
            {
                element: (
                    <ModuleAuthenticatedProviders>
                        <Outlet />
                    </ModuleAuthenticatedProviders>
                ),
                children: registerPrivateRoutes(runtime)
            }
        ]
    });
};

const registerNavigationItems: ModuleRegisterFunction = () => {
    return (deferredRuntime, { currentWorkspace }) => {
        if (!currentWorkspace?.id) {
            return;
        }

        const paths = getPaths({ workspaceId: currentWorkspace.id });

        registerProtectSidebarItem(deferredRuntime, {
            id: "migrate",
            label: "Migrate",
            to: paths.root,
            icon: <SyncIcon size="md" />,
            priority: ProtectSidebarItemPriority.Migrate
        });
    };
};

export const register: ModuleRegisterFunction = async runtime => {
    if (runtime.isMswEnabled) {
        const { handlers } = await import("#api/msw.ts");

        // Tags handlers so DUMP_MSW_MOCKS=true (pnpm dev-msw:dump) can report which module each mock came from.
        handlers.forEach(handler => Object.assign(handler, { mockModule: "move" }));

        runtime.registerRequestHandlers(handlers);
    }

    await registerRoutes(runtime);

    return registerNavigationItems(runtime);
};
